import React, { useCallback } from "react";

import { Switch, Route, useRouteMatch } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { getClientFeatureQuery } from "../../../../config/graphql/query";
import { TOGGLE_CLIENT_FEATURE } from "../../../../config/graphql/mutation";

import useHasRole from "../../../../lib/hooks/useHasRole";
import { useClientId } from "../hooks/useClient";
import { ToggleFeatureButton } from "../Shelly/ShellyControllers";

import VisitorFieldList from "./List";
import VisitorFieldForm from "./Field";

const FEATURE_NAME = "Pre-registration";
const FEATURE_KEY = "preRegistration";

const VisitorPreregistrationRoutes: React.FC = () => {
  const match = useRouteMatch();

  return (
    <Switch>
      <Route exact path={match.path}>
        <VisitorFieldList />
      </Route>
      <Route exact path={`${match.path}/new`}>
        <VisitorFieldForm />
      </Route>
      <Route exact path={`${match.path}/:fieldId`}>
        <VisitorFieldForm />
      </Route>
    </Switch>
  );
};

const VisitorPreregistration: React.FC = () => {
  const { t } = useTranslation(["common"]);

  const clientId = useClientId();

  const canToggle = useHasRole(["ADMIN", "CLIENT_ADMIN"]);

  const { data } = useQuery(getClientFeatureQuery(FEATURE_KEY), {
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const [toggleFeature] = useMutation(TOGGLE_CLIENT_FEATURE, {
    refetchQueries: [
      {
        query: getClientFeatureQuery(FEATURE_KEY),
        variables: { id: clientId },
      },
    ],
  });

  const enabled = data?.client?.preferences?.preRegistration?.enabled ?? false;

  const onToggle = useCallback(
    async (next: boolean) => {
      try {
        await toggleFeature({
          variables: {
            input: {
              id: clientId,
              feature: FEATURE_KEY,
              enabled: next,
            },
          },
        });
        toast.success<string>(
          t(`common:premiumFeature.${next ? "enabled" : "disabled"}`, {
            feature: FEATURE_NAME,
          }),
        );
      } catch (e) {
        toast.error<string>(
          t(`common:premiumFeature.${next ? "enabled" : "disabled"}Error`, {
            feature: FEATURE_NAME,
          }),
        );
      }
    },
    [clientId, toggleFeature, t],
  );

  if (!enabled) {
    return (
      <div className="container-fluid">
        {canToggle ? (
          <ToggleFeatureButton
            featureName={FEATURE_NAME}
            onClick={() => onToggle(true)}
          />
        ) : (
          <div className="text-muted ml-3">
            <h5 className="mb-3">{t("common:premiumFeature.title")}</h5>
            <p>{t("common:premiumFeature.text")}</p>
            <p>{t("common:premiumFeature.contact")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <VisitorPreregistrationRoutes />
      {canToggle && (
        <div className="container-fluid my-3">
          <ToggleFeatureButton
            featureName={FEATURE_NAME}
            disable
            onClick={() => onToggle(false)}
          />
        </div>
      )}
    </>
  );
};

export default React.memo(VisitorPreregistration);
